/**
 * Collapsible section header for a filter panel.
 *
 * Renders a pressable row with section title, optional active count badge,
 * and a chevron on the right side. The SHARED implementation across every
 * filter surface — Watchlist's `FilterSheet`, the Search Player modal's
 * `InlineFilterPanel`, and Pipeline's `AddPlayersFilterPanel` — per the
 * standing "same component, not a copy" acceptance criteria on
 * SE-6867/SE-6871/SE-7144. A prior Pipeline-only fork
 * (`player-kanban/.../FilterSectionHeader`) drifted on font, size, weight,
 * and badge implementation despite claiming parity in its own doc comment;
 * it has been deleted in favor of this one. Do not fork this component
 * again — extend it (see `inset`) instead.
 * @module features/scouting/components/molecules/SectionHeader/SectionHeaderView
 */

import { ChevronDownIcon, ChevronRightIcon } from "lucide-react-native";
import React, { memo } from "react";

import { TonePill } from "@/components/atoms/Badge";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Props for the SectionHeaderView component */
interface SectionHeaderViewProps {
  /** Section title text */
  readonly title: string;
  /** Whether the section content is expanded */
  readonly isExpanded: boolean;
  /** Toggle section expand/collapse */
  readonly onToggle: () => void;
  /** Number of active filters in this section */
  readonly activeCount: number;
  /** Test identity forwarded to the pressable row. */
  readonly testID?: string;
  /**
   * Whether the row owns its own horizontal gutter (`px-6`). Defaults to
   * `true` for callers whose shell renders edge-to-edge. A caller whose own
   * shell already insets its content (e.g. a drawer with a 20px padding)
   * passes `false` so the two gutters don't stack.
   */
  readonly inset?: boolean;
}

/**
 * Renders a collapsible section header with count badge and right-side chevron
 * @param root0 - Component props
 * @param root0.title - Section title
 * @param root0.isExpanded - Whether section is expanded
 * @param root0.onToggle - Toggle section
 * @param root0.activeCount - Number of active filters
 * @param root0.testID - Test identity
 * @param root0.inset - Whether the row owns its own horizontal gutter
 */
const SectionHeaderView = ({
  title,
  isExpanded,
  onToggle,
  activeCount,
  testID,
  inset = true,
}: SectionHeaderViewProps) => (
  <Pressable
    testID={testID}
    onPress={onToggle}
    accessibilityRole="button"
    aria-expanded={isExpanded}
    className={`flex-row items-center justify-between gap-3 py-4 ${inset ? "px-6" : ""}`.trim()}
  >
    <Stack direction="horizontal" className="flex-1 items-center gap-2">
      <Text variant="title-sm-medium" className="text-content-secondary">
        {title}
      </Text>
      {activeCount > 0 ? (
        <TonePill
          tone="accent"
          label={String(activeCount)}
          textVariant="micro"
          className="size-5"
        />
      ) : null}
    </Stack>
    <Icon
      as={isExpanded ? ChevronDownIcon : ChevronRightIcon}
      size="sm"
      className="text-content-subtle"
    />
  </Pressable>
);

SectionHeaderView.displayName = "SectionHeaderView";

export default memo(SectionHeaderView);
